import { Link, Outlet, useLocation } from 'react-router-dom'
import { ClipboardList, History, Settings } from 'lucide-react'
import { OperatorThemeProvider } from '../../hooks/useOperatorTheme'
import { cn } from '../../lib/utils'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup
} from '../../../components/ui/resizable'
import { FeedbackDialog } from '../../../components/Feedback/FeedbackDialog'
import { AssistChat } from '../AssistChat/AssistChat'

interface NavItem {
  id: string
  label: string
  to: string
  icon: 'orders' | 'releases' | 'settings'
}

const NAV: NavItem[] = [
  { id: 'orders', label: 'Work Orders', to: '/', icon: 'orders' },
  {
    id: 'releases',
    label: 'Release history',
    to: '/releases',
    icon: 'releases'
  },
  { id: 'settings', label: 'Settings', to: '/settings', icon: 'settings' }
]

function NavIcon({ name }: { name: NavItem['icon'] }) {
  if (name === 'settings') return <Settings className="size-5" strokeWidth={1.6} />
  if (name === 'releases') return <History className="size-5" strokeWidth={1.6} />
  return <ClipboardList className="size-5" strokeWidth={1.6} />
}

/**
 * Technician shell: Work Orders + Settings sidebar, main pane, Assist rail.
 */
export default function AppLayout() {
  const location = useLocation()
  const hideChat =
    location.pathname.startsWith('/settings') || location.pathname.startsWith('/releases')

  return (
    <OperatorThemeProvider>
      <div className="tv-shell">
        <aside className="tv-sidebar" aria-label="Technician navigation">
          <div className="tv-sidebar-logo" aria-hidden>
            Axilon
          </div>
          <nav className="tv-sidebar-nav">
            {NAV.map((item) => {
              const active =
                item.to === '/'
                  ? location.pathname === '/' || location.pathname.startsWith('/work-orders')
                  : location.pathname.startsWith(item.to)
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  className={cn('tv-nav-btn', active && 'active')}
                  aria-current={active ? 'page' : undefined}
                >
                  <NavIcon name={item.icon} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>
          <FeedbackDialog audience="operator" />
        </aside>

        <div className="tv-main">
          {hideChat ? (
            <div className="tv-content tv-no-chat">
              <Outlet />
            </div>
          ) : (
            <ResizablePanelGroup orientation="horizontal" className="tv-content h-full min-h-0">
              <ResizablePanel defaultSize="70%" minSize="40%">
                <Outlet />
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize="30%" minSize="17.5rem">
                <AssistChat />
              </ResizablePanel>
            </ResizablePanelGroup>
          )}
        </div>
      </div>
    </OperatorThemeProvider>
  )
}
